/**
 * DeveloperConnectGuide — how to point an AI tool (or a script) at this
 * platform, as copy-ready snippets.
 *
 * The platform is itself the MCP server (`<api>/api/v1/mcp`), so Claude Code
 * and Codex connect by URL with nothing installed; Claude Desktop goes through
 * the stdio bridge (mcp-server/); a script uses the same token as a Bearer on
 * the REST API. Everything here names the app as this organization calls it
 * ("Jewish Pro" for M² Team) — the wording, the key the server gets in the
 * client's config and the Codex token variable — taken from the server's own
 * answer (`GET /auth/tokens` → `app`, handed down by AccessTokensPanel), so the
 * snippet's key IS the name the server introduces itself by; nothing here works
 * it out a second time. Shown twice: in Settings with a placeholder, and in the one-time token
 * reveal with the real token filled in — the only moment it exists in full.
 *
 * Renders as `.security-item` rows so it sits inside the Settings page's
 * `.security-card` next to the token rows; the caller supplies the card.
 */

import { useMemo, useState } from 'react';
import { message } from 'antd';
import { ApiOutlined, CopyOutlined } from '@ant-design/icons';

import { useLanguage } from '@hooks/useLanguage';
import configService from '@/services/config';
import type { AppIdentity } from '../services/accessTokenService';

type Client = 'claudeCode' | 'codex' | 'claudeDesktop' | 'rest';
const CLIENTS: Client[] = ['claudeCode', 'codex', 'claudeDesktop', 'rest'];

export interface DeveloperConnectGuideProps {
  /** What this person calls the platform, as the server answers it. */
  app: AppIdentity;
  /** The freshly minted token, in the one-time reveal; a placeholder otherwise. */
  token?: string;
}

/** Copy to the clipboard, saying so either way. Clipboard access can be
 *  refused (insecure context, permissions): the text is still on screen. */
export async function copyText(value: string, t: (key: string) => string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    message.success(t('accessTokens.copied'));
  } catch {
    message.warning(t('accessTokens.copyFailed'));
  }
}

/** The API origin the browser already talks to, e.g. https://api.example.com */
function apiOrigin(): string {
  const base = configService.getApiBaseUrl();                // …/api/v1
  const absolute = new URL(base, window.location.origin).toString();
  return absolute.replace(/\/api\/v1\/?$/, '');
}

/** The Codex variable holding the token: the app's id, e.g. JEWISH_PRO_TOKEN. */
function tokenEnvVar(key: string): string {
  return `${key.replace(/-/g, '_').toUpperCase()}_TOKEN`;
}

/** `key` is the name the server gets in the client's config — the app's id. */
function snippet(client: Client, origin: string, token: string, key: string, envVar: string): string {
  const url = `${origin}/api/v1/mcp`;
  switch (client) {
    case 'claudeCode':
      return `claude mcp add --transport http ${key} ${url} \\\n  --header "Authorization: Bearer ${token}"`;
    case 'codex':
      return `# ~/.codex/config.toml   (export ${envVar}=${token})\n[mcp_servers.${key}]\nurl = "${url}"\nbearer_token_env_var = "${envVar}"`;
    case 'claudeDesktop':
      return JSON.stringify({
        mcpServers: {
          [key]: {
            command: 'node',
            args: ['/path/to/mcp-server/src/index.js'],
            env: { MCP_SERVER_URL: origin, MCP_SERVER_TOKEN: token },
          },
        },
      }, null, 2);
    case 'rest':
    default:
      return `curl -H "Authorization: Bearer ${token}" ${origin}/api/v1/mcp/whoami\ncurl -H "Authorization: Bearer ${token}" "${origin}/api/v1/mcp/catalog?tag=agents"`;
  }
}

export default function DeveloperConnectGuide({ app: identity, token }: DeveloperConnectGuideProps) {
  const { t } = useLanguage();
  const [client, setClient] = useState<Client>('claudeCode');
  const origin = useMemo(apiOrigin, []);
  const app = identity.name;
  const envVar = tokenEnvVar(identity.id);
  const endpoint = `${origin}/api/v1/mcp`;
  const code = snippet(client, origin, token || '<your-token>', identity.id, envVar);

  return (
    <div className="dev-connect" data-testid="dev-connect-guide">
      <div className="security-item">
        <div className="security-icon">
          <ApiOutlined />
        </div>
        <div className="security-content">
          <h3 className="security-title">{t('accessTokens.connect.title')}</h3>
          <p className="security-description">{t('accessTokens.connect.intro', { app })}</p>
        </div>
      </div>

      {/* The endpoint, the tool picker, the hint and the command — one row,
          stacked under the tile's column so it reads as the row's body. */}
      <div className="security-item dev-connect__body">
        <div className="dev-connect__endpoint" data-testid="dev-connect-endpoint">
          <span className="dev-connect__endpoint-label">{t('accessTokens.connect.endpointLabel')}</span>
          <code className="dev-connect__endpoint-url" dir="ltr">{endpoint}</code>
          <button
            type="button"
            className="dev-connect__icon-btn"
            onClick={() => copyText(endpoint, t)}
            title={t('accessTokens.copy')}
            aria-label={t('accessTokens.copy')}
            data-testid="dev-connect-endpoint-copy"
          >
            <CopyOutlined />
          </button>
        </div>

        <div className="language-switch dev-connect__clients" role="tablist" data-testid="dev-connect-client">
          {CLIENTS.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={client === c}
              className={`language-option ${client === c ? 'active' : ''}`}
              onClick={() => setClient(c)}
            >
              {t(`accessTokens.connect.client.${c}`)}
            </button>
          ))}
        </div>

        <p className="security-description dev-connect__hint">
          {t(`accessTokens.connect.hint.${client}`, { app, envVar })}
        </p>

        <div className="dev-connect__code-wrap" dir="ltr">
          <pre className="dev-connect__code" data-testid="dev-connect-snippet">{code}</pre>
          <button
            type="button"
            className="dev-connect__icon-btn dev-connect__code-copy"
            onClick={() => copyText(code, t)}
            title={t('accessTokens.copy')}
            aria-label={t('accessTokens.copy')}
            data-testid="dev-connect-copy"
          >
            <CopyOutlined />
            <span>{t('accessTokens.copy')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
